/**
 * 版本管理 Tab
 * v9.2.2: 层级结构 + 撤回功能
 * 
 * - 列表、发布、对比、回滚
 * - 层级显示：Identity 版本 → 包含的 Facet 版本
 * - 撤回当前版本：需要二次确认输入"撤回"
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
  History,
  GitCompare,
  RotateCcw,
  Plus,
  Loader2,
  RefreshCw,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Undo2,
  AlertTriangle,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService } from '@/modules/identity/services'
import type { IdentityVersion, VersionCompareResult } from '@/modules/identity/types'
import { SCENE_TAG_CONFIG, type SceneTag } from '@/modules/identity/types/facet'

interface VersionTabProps {
  identityId: string
  currentVersion: number
}

export function VersionTab({ identityId, currentVersion }: VersionTabProps) {
  const { showSuccess, showError } = useToast()

  // 状态
  const [versions, setVersions] = useState<IdentityVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [compareResult, setCompareResult] = useState<VersionCompareResult | null>(null)
  
  // 展开状态（记录哪些版本被展开）
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(new Set())

  // 弹窗状态
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [compareDialogOpen, setCompareDialogOpen] = useState(false)
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false)
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState<IdentityVersion | null>(null)

  // 操作状态
  const [publishing, setPublishing] = useState(false)
  const [comparing, setComparing] = useState(false)
  const [rollingBack, setRollingBack] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)

  // 发布表单
  const [publishDescription, setPublishDescription] = useState('')
  
  // 撤回确认输入
  const [withdrawConfirmInput, setWithdrawConfirmInput] = useState('')

  // 对比选择
  const [compareFrom, setCompareFrom] = useState<number | null>(null)
  const [compareTo, setCompareTo] = useState<number | null>(null)

  // ==================== 数据加载 ====================
  const loadVersions = useCallback(async () => {
    setLoading(true)
    try {
      const response = await IdentityApiService.listVersions(identityId)
      // 后端返回的是数组直接在 data 中
      const versionList = Array.isArray(response.data) ? response.data : []
      setVersions(versionList)
      
      // 默认展开当前版本
      const currentVer = versionList.find(v => v.is_current)
      if (currentVer) {
        setExpandedVersions(new Set([currentVer.version]))
      }
    } catch (error) {
      console.error('Failed to load versions:', error)
      showError('无法加载版本历史', '加载失败')
    } finally {
      setLoading(false)
    }
  }, [identityId, showError])

  useEffect(() => {
    loadVersions()
  }, [loadVersions])

  // ==================== 展开/折叠 ====================
  const toggleExpand = (versionNum: number) => {
    setExpandedVersions(prev => {
      const next = new Set(prev)
      if (next.has(versionNum)) {
        next.delete(versionNum)
      } else {
        next.add(versionNum)
      }
      return next
    })
  }

  // ==================== 发布版本 ====================
  const handlePublish = useCallback(async () => {
    if (!publishDescription.trim()) {
      showError('请输入版本说明', '验证失败')
      return
    }

    setPublishing(true)
    try {
      await IdentityApiService.publishVersion(identityId, {
        description: publishDescription.trim(),
      })
      showSuccess('新版本已发布', '发布成功')
      setPublishDialogOpen(false)
      setPublishDescription('')
      loadVersions()
    } catch (error) {
      console.error('Failed to publish:', error)
      showError('发布失败', '操作失败')
    } finally {
      setPublishing(false)
    }
  }, [identityId, publishDescription, showSuccess, showError, loadVersions])

  // ==================== 版本对比 ====================
  const handleCompare = useCallback(async () => {
    if (!compareFrom || !compareTo) {
      showError('请选择要对比的版本', '验证失败')
      return
    }

    setComparing(true)
    try {
      const response = await IdentityApiService.compareVersions(
        identityId,
        compareFrom,
        compareTo
      )
      setCompareResult(response.data)
    } catch (error) {
      console.error('Failed to compare:', error)
      showError('对比失败', '操作失败')
    } finally {
      setComparing(false)
    }
  }, [identityId, compareFrom, compareTo, showError])

  // ==================== 回滚 ====================
  const handleRollbackClick = (version: IdentityVersion) => {
    setSelectedVersion(version)
    setRollbackDialogOpen(true)
  }

  const handleRollbackConfirm = useCallback(async () => {
    if (!selectedVersion) return

    setRollingBack(true)
    try {
      await IdentityApiService.rollbackVersion(identityId, selectedVersion.version)
      showSuccess(`已回滚到 v${selectedVersion.version}`, '回滚成功')
      setRollbackDialogOpen(false)
      setSelectedVersion(null)
      loadVersions()
    } catch (error) {
      console.error('Failed to rollback:', error)
      showError('回滚失败', '操作失败')
    } finally {
      setRollingBack(false)
    }
  }, [identityId, selectedVersion, showSuccess, showError, loadVersions])

  // ==================== 撤回当前版本 ====================
  const handleWithdrawClick = (version: IdentityVersion) => {
    setSelectedVersion(version)
    setWithdrawConfirmInput('')
    setWithdrawDialogOpen(true)
  }

  const handleWithdrawConfirm = useCallback(async () => {
    if (!selectedVersion) return
    
    if (withdrawConfirmInput !== '撤回') {
      showError('请输入 "撤回" 进行确认', '验证失败')
      return
    }

    setWithdrawing(true)
    try {
      // 撤回 = 回滚到上一个版本
      // 如果只有一个版本，则无法撤回
      const previousVersion = versions.find(v => v.version === selectedVersion.version - 1)
      if (!previousVersion) {
        showError('没有可回滚的历史版本', '撤回失败')
        setWithdrawing(false)
        return
      }
      
      await IdentityApiService.rollbackVersion(identityId, previousVersion.version)
      showSuccess(`已撤回版本 v${selectedVersion.version}，当前版本回退到 v${previousVersion.version}`, '撤回成功')
      setWithdrawDialogOpen(false)
      setSelectedVersion(null)
      setWithdrawConfirmInput('')
      loadVersions()
    } catch (error) {
      console.error('Failed to withdraw:', error)
      showError('撤回失败', '操作失败')
    } finally {
      setWithdrawing(false)
    }
  }, [identityId, selectedVersion, withdrawConfirmInput, versions, showSuccess, showError, loadVersions])

  // ==================== 渲染状态徽章 ====================
  const renderStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string; label: string }> = {
      active: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: '已发布' },
      draft: { bg: 'bg-amber-50', text: 'text-amber-700', label: '草稿' },
      not_started: { bg: 'bg-slate-50', text: 'text-slate-500', label: '未开始' },
      learning: { bg: 'bg-blue-50', text: 'text-blue-700', label: '学习中' },
      failed: { bg: 'bg-red-50', text: 'text-red-700', label: '失败' },
    }
    const c = config[status] || config.not_started
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    )
  }

  // ==================== 渲染 ====================
  return (
    <div className="space-y-4">
      {/* 版本列表 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                版本历史
              </CardTitle>
              <CardDescription>
                当前版本：v{currentVersion}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCompareFrom(null)
                  setCompareTo(null)
                  setCompareResult(null)
                  setCompareDialogOpen(true)
                }}
                disabled={versions.length < 2}
              >
                <GitCompare className="h-4 w-4 mr-2" />
                版本对比
              </Button>
              <Button
                size="sm"
                onClick={() => setPublishDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                发布版本
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadVersions()}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-violet-600" />
              <p className="text-sm text-slate-500">加载中...</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <History className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="mb-2">暂无版本记录</p>
              <p className="text-sm">完成人格学习后，可以发布版本</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => {
                const isExpanded = expandedVersions.has(version.version)
                const hasFacets = version.facet_versions && version.facet_versions.length > 0
                
                return (
                  <Collapsible
                    key={version.version}
                    open={isExpanded}
                    onOpenChange={() => toggleExpand(version.version)}
                  >
                    <div
                      className={`rounded-lg border transition-all ${
                        version.is_current
                          ? 'bg-violet-50 border-violet-200'
                          : 'bg-slate-50 border-slate-100 hover:border-slate-200'
                      }`}
                    >
                      {/* 版本主行 */}
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-4 cursor-pointer">
                          <div className="flex items-center gap-4">
                            {/* 展开/折叠图标 */}
                            <div className="w-5 h-5 flex items-center justify-center text-slate-400">
                              {hasFacets ? (
                                isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )
                              ) : (
                                <span className="w-4" />
                              )}
                            </div>
                            
                            {/* 版本号和标签 */}
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-slate-700">
                                v{version.version}
                              </span>
                              {version.is_current && (
                                <Badge className="bg-violet-500">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  当前
                                </Badge>
                              )}
                            </div>
                            
                            {/* 版本描述和时间 */}
                            <div>
                              <p className="text-sm text-slate-600">
                                {version.description}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">
                                {new Date(version.created_at).toLocaleString('zh-CN')}
                              </p>
                            </div>
                          </div>
                          
                          {/* 操作按钮 */}
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            {version.is_current ? (
                              // 当前版本：始终显示撤回按钮，只有一个版本时禁用
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleWithdrawClick(version)}
                                disabled={versions.length <= 1}
                                className="text-orange-600 border-orange-200 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={versions.length <= 1 ? '没有可回退的历史版本' : '撤回当前版本'}
                              >
                                <Undo2 className="h-4 w-4 mr-2" />
                                撤回
                              </Button>
                            ) : (
                              // 非当前版本：显示回滚按钮
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRollbackClick(version)}
                              >
                                <RotateCcw className="h-4 w-4 mr-2" />
                                回滚
                              </Button>
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      {/* 展开内容：Facet 版本列表 */}
                      <CollapsibleContent>
                        {hasFacets && (
                          <div className="px-4 pb-4 pt-0">
                            <div className="ml-9 pl-4 border-l-2 border-slate-200">
                              <p className="text-xs font-medium text-slate-500 mb-2">
                                包含 {version.facet_versions!.length} 个场景人格版本
                              </p>
                              <div className="space-y-2">
                                {version.facet_versions!.map((facet) => {
                                  const config = SCENE_TAG_CONFIG[facet.scene_tag as SceneTag]
                                  return (
                                    <div
                                      key={facet.scene_tag}
                                      className="flex items-center justify-between p-2 bg-white rounded border border-slate-100"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{config?.icon || '📋'}</span>
                                        <span className="text-sm font-medium text-slate-700">
                                          {config?.label || facet.scene_name}
                                        </span>
                                        {facet.version && (
                                          <span className="text-xs font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                            v{facet.version}
                                          </span>
                                        )}
                                      </div>
                                      {renderStatusBadge(facet.status)}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 发布版本弹窗 */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>发布新版本</DialogTitle>
            <DialogDescription>
              将当前配置发布为新版本 v{currentVersion + 1}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>版本说明 *</Label>
              <Input
                value={publishDescription}
                onChange={(e) => setPublishDescription(e.target.value)}
                placeholder="描述本次更新的内容..."
                maxLength={200}
              />
              <p className="text-xs text-slate-400">
                {publishDescription.length}/200
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              {publishing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              发布
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 版本对比弹窗 */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>版本对比</DialogTitle>
            <DialogDescription>
              选择两个版本进行对比
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-2">
                <Label>起始版本</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={compareFrom || ''}
                  onChange={(e) => setCompareFrom(Number(e.target.value) || null)}
                >
                  <option value="">选择版本</option>
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version} - {v.description}
                    </option>
                  ))}
                </select>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400 mt-6" />
              <div className="flex-1 space-y-2">
                <Label>目标版本</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={compareTo || ''}
                  onChange={(e) => setCompareTo(Number(e.target.value) || null)}
                >
                  <option value="">选择版本</option>
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version} - {v.description}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleCompare}
                disabled={!compareFrom || !compareTo || comparing}
                className="mt-6"
              >
                {comparing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  '对比'
                )}
              </Button>
            </div>

            {/* 对比结果 */}
            {compareResult && (
              <div className="border rounded-lg p-4 bg-slate-50">
                <h4 className="font-medium mb-3">
                  v{compareResult.from_version} → v{compareResult.to_version} 变更
                </h4>
                {compareResult.changes.length === 0 ? (
                  <p className="text-sm text-slate-500">无变更</p>
                ) : (
                  <div className="space-y-2">
                    {compareResult.changes.map((change, i) => (
                      <div
                        key={i}
                        className={`p-2 rounded text-sm ${
                          change.type === 'added'
                            ? 'bg-green-50 text-green-700'
                            : change.type === 'removed'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        <span className="font-mono text-xs">{change.path}</span>
                        <span className="mx-2">
                          {change.type === 'added' ? '+ 新增' : change.type === 'removed' ? '- 删除' : '~ 修改'}
                        </span>
                        {change.value !== undefined && (
                          <span className="text-xs">
                            {String(change.value)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompareDialogOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 回滚确认弹窗 */}
      <Dialog open={rollbackDialogOpen} onOpenChange={setRollbackDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认回滚</DialogTitle>
            <DialogDescription>
              确定要回滚到 v{selectedVersion?.version} 吗？这将创建一个新版本并恢复该版本的配置。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleRollbackConfirm} disabled={rollingBack}>
              {rollingBack && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              确认回滚
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 撤回确认弹窗 - 需要输入"撤回" */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              确认撤回版本
            </DialogTitle>
            <DialogDescription>
              您确定要撤回当前版本 v{selectedVersion?.version} 吗？
              <br />
              撤回后将回退到上一个版本 v{selectedVersion ? selectedVersion.version - 1 : '?'}。
              <br />
              <strong>请在下方输入 "撤回" 以确认此操作。</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="请输入 '撤回' 进行确认"
              value={withdrawConfirmInput}
              onChange={(e) => setWithdrawConfirmInput(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleWithdrawConfirm}
              disabled={withdrawConfirmInput !== '撤回' || withdrawing}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {withdrawing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              确认撤回
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
